import { forwardRef } from 'react';
import clsx from 'clsx';

import { VisuallyHidden } from './VisuallyHidden';
import styles from './Spinner.module.css';

export interface SpinnerProps {
  /** `sm` = --c-icon-16, `md` = --c-icon-20. */
  size?: 'sm' | 'md';
  /**
   * Announced status text. `null` marks the spinner decorative — use that only
   * when a surrounding element already carries `aria-busy` or its own status
   * text, as `Button` does while loading.
   */
  label?: string | null;
  className?: string;
}

/**
 * Under `prefers-reduced-motion` the ring stops turning (see the stylesheet).
 * That is why the label is not optional by default: the text, not the motion,
 * is what makes "busy" legible.
 */
export const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>(function Spinner(
  { size = 'sm', label = 'Loading', className },
  ref,
) {
  const decorative = label === null;

  return (
    <span
      ref={ref}
      className={clsx(styles.spinner, size === 'md' && styles.md, className)}
      role={decorative ? undefined : 'status'}
      aria-hidden={decorative ? true : undefined}
    >
      <span className={styles.ring} />
      {decorative ? null : <VisuallyHidden>{label}</VisuallyHidden>}
    </span>
  );
});
